/**
 * Dispute intelligence API client — resume, eligibility, report changes, AI recommendations.
 */

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const BASE = `${BACKEND_URL}/api/dispute-intelligence`;

async function fetchJson(path, { token, method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const err = await response.json();
      detail = err.detail || err.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return response.json();
}

export const disputeIntelligenceService = {
  getResumeCandidate(token) {
    return fetchJson('/resume-candidate', { token });
  },

  saveWizardProgress(roundId, payload, token) {
    return fetchJson(`/rounds/${roundId}/wizard-progress`, {
      token,
      method: 'PUT',
      body: payload,
    });
  },

  getRoundEligibility(token) {
    return fetchJson('/round-eligibility', { token });
  },

  getReportChanges(token) {
    return fetchJson('/report-changes', { token });
  },

  getAiRecommendations(token) {
    return fetchJson('/ai-recommendations', { token });
  },

  getRefreshMeta(token) {
    return fetchJson('/credit-report/refresh-meta', { token });
  },

  triggerReportRefresh(token) {
    return fetchJson('/credit-report/refresh', { token, method: 'POST' });
  },

  async uploadInvestigationResults(formData, token) {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${BASE}/investigation-results`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  },

  attachInvestigationDocument({ bureau, documentId, investigationCompleted = true, notes = '' }, token) {
    const formData = new FormData();
    formData.append('bureau', bureau || 'experian');
    formData.append('investigation_completed', investigationCompleted ? 'true' : 'false');
    formData.append('notes', notes);
    formData.append('document_id', documentId);
    return this.uploadInvestigationResults(formData, token);
  },

  detachInvestigationResults(submissionId, token) {
    const query = submissionId ? `?submission_id=${encodeURIComponent(submissionId)}` : '';
    return fetchJson(`/investigation-results/active${query}`, { token, method: 'DELETE' });
  },
};
